<!-- Responsibility: Render a dedicated location info dialog with zoomable image, NPCs, items, and quests.
Scope: Location dialog content only; modal lifecycle is owned by game-modal/app-shell. -->
<location-dialog>
  <header class="fm-header" if={ title() || meta() }>
    <div class="fm-header-copy">
      <h2 if={ title() }>{ title() }</h2>
      <raw-html if={ meta() } content={ meta() } mode="prose" class="fm-meta"></raw-html>
    </div>
    <button class="close-btn modal-close-hit" aria-label="Close location dialog" onclick={ closeDialog }>X</button>
  </header>

  <raw-html if={ desc() } content={ desc() } mode="prose" class="fm-desc"></raw-html>

  <div class="ld-image-wrap" if={ media() }>
    <img
      if={ media().imageUrl }
      class="ld-image { state.zoomed ? 'zoomed' : '' }"
      src={ media().imageUrl }
      alt={ props.content?.locationName || '' }
      onclick={ toggleZoom }
    >
    <div if={ media().imageLoading && !media().imageUrl } class="ld-image-placeholder">
      <span class="ld-spinner"></span>
    </div>
    <div if={ !media().imageLoading && !media().imageUrl } class="ld-image-fallback">
      <span>{ media().fallbackLabel || '?' }</span>
    </div>
  </div>

  <raw-html if={ conditions() } content={ conditions() } mode="prose" class="ld-conditions"></raw-html>

  <div if={ npcs().length } class="ld-section">
    <div class="ld-section-label">WHO IS / WAS THERE</div>
    <div
      each={ npc in npcs() }
      class="fm-card npc { canInteract() ? '' : 'disabled' }"
      onclick={ () => canInteract() && onOpenNpc(npc) }
    >
      <b>{ npc.name }</b>
      <span class="fm-card-sub">{ npc.identity }</span>
    </div>
  </div>

  <div if={ items().length } class="ld-section">
    <div class="ld-section-label">WHAT IS / WAS THERE</div>
    <div
      each={ item in items() }
      class="fm-card item { canInteract() ? '' : 'disabled' }"
      onclick={ () => canInteract() && onOpenItem(item) }
    >
      <object-visual name={ item } size="md"></object-visual>
      <span class="fm-card-arrow">&#x25BC;</span>
    </div>
  </div>

  <div if={ quests().length } class="ld-section">
    <div class="ld-section-label">RELATED QUESTS</div>
    <ul class="ld-quest-list">
      <li each={ quest in quests() }>
        <b>{ quest.title }</b>: { quest.desc }
      </li>
    </ul>
  </div>

  <footer if={ actions().length } class="modal-actions">
    <div class="fm-chips">
      <button
        each={ action in actions() }
        class="fm-chip { action.danger ? 'danger' : '' }"
        onclick={ () => onAction(action) }
      >{ action.label }</button>
    </div>
  </footer>

  <script>
    import { resolveDialogImage } from '../../dialog/dialog-media.ts'
    import { runUiAction } from '../../action-manager.ts'
    import ObjectVisual from '../object-visual.riot'
    import RawHtml from '../raw-html.riot'

    export default {
      components: { ObjectVisual, RawHtml },
      state: { media: null, zoomed: false },

      onMounted() {
        this.syncMedia()
      },

      onUpdated() {
        this.syncMedia()
      },

      media() {
        return this.state.media || this.props.content?.media || null
      },

      npcs() {
        return Array.isArray(this.props.content?.npcs) ? this.props.content.npcs : []
      },

      items() {
        return Array.isArray(this.props.content?.items) ? this.props.content.items : []
      },

      quests() {
        return Array.isArray(this.props.content?.quests) ? this.props.content.quests : []
      },

      conditions() {
        return this.props.content?.conditions || ''
      },

      title() {
        return String(this.props.options?.title ?? this.props.content?.title ?? this.props.content?.locationName ?? '').trim()
      },

      desc() {
        return String(this.props.options?.desc ?? this.props.content?.desc ?? '').trim()
      },

      meta() {
        return String(this.props.options?.meta ?? this.props.content?.meta ?? '').trim()
      },

      actions() {
        return Array.isArray(this.props.options?.actions) ? this.props.options.actions : []
      },

      canInteract() {
        return this.props.content?.allowEntityInteraction !== false
      },

      toggleZoom() {
        this.update({ zoomed: !this.state.zoomed })
      },

      onOpenNpc(npc) {
        if (typeof this.props.content?.onOpenNpc === 'function') this.props.content.onOpenNpc(npc)
      },

      onOpenItem(item) {
        if (typeof this.props.content?.onOpenItem === 'function') this.props.content.onOpenItem(item)
      },

      async closeDialog() {
        await this.props.requestModalClose?.('close-button')
      },

      async onAction(action) {
        const closed = await this.props.requestModalClose?.('action')
        if (closed === false) return
        await runUiAction(action, { initiatorId: 'location-dialog' })
      },

      async syncMedia() {
        const media = this.props.content?.media || null
        if (!media) {
          if (this.state.media) this.update({ media: null })
          this.mediaKey = ''
          return
        }

        const directUrl = media.imageUrl || null
        const imageKind = media.imageKind
        const imageSeed = media.imageSeed
        const mediaKey = directUrl ? ('url:' + directUrl) : (String(imageKind || '') + ':' + String(imageSeed || '') + ':' + String(media.fallbackLabel || ''))
        if (mediaKey === this.mediaKey) return
        this.mediaKey = mediaKey

        if (directUrl || !imageKind || !imageSeed) {
          this.update({ media: { ...media, imageUrl: directUrl, imageLoading: false } })
          return
        }

        this.update({ media: { ...media, imageUrl: null, imageLoading: true } })
        try {
          const imageUrl = await resolveDialogImage(imageKind, imageSeed)
          if (this.mediaKey !== mediaKey) return
          this.update({ media: { ...media, imageUrl, imageLoading: false } })
        } catch (error) {
          console.error('Location media load failed:', error)
          if (this.mediaKey !== mediaKey) return
          this.update({ media: { ...media, imageUrl: null, imageLoading: false } })
        }
      },
    }
  </script>

  <style>
    location-dialog {
      display: block;
    }

    .fm-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: var(--space-md);
      margin-bottom: var(--space-sm);
    }

    .fm-header-copy {
      min-width: 0;
    }

    .fm-header h2 {
      margin: 0 0 var(--space-xs);
    }

    .ld-image-wrap {
      width: 100%;
      margin: var(--space-xs) 0 var(--space-md);
      border-radius: 0.625rem;
      overflow: hidden;
      background: color-mix(in srgb, var(--surface-card-2, #111) var(--alpha-high), var(--bg));
    }

    .ld-image {
      display: block;
      width: 100%;
      height: auto;
      max-height: min(50vh, 22.5rem);
      object-fit: contain;
      cursor: zoom-in;
      transition: max-height 0.3s ease, transform 0.3s ease;
      border-radius: 0.625rem;
    }

    .ld-image.zoomed {
      max-height: none;
      cursor: zoom-out;
      transform: scale(1);
    }

    .ld-image-placeholder {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 7.5rem;
      color: var(--gold);
    }

    .ld-spinner {
      display: inline-block;
      width: 1.4rem;
      height: 1.4rem;
      border: 0.125rem solid color-mix(in srgb, var(--gold) 30%, transparent);
      border-top-color: var(--gold);
      border-radius: 50%;
      animation: spin 0.7s linear infinite;
    }

    .ld-image-fallback {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 5rem;
      font-size: var(--space-lg);
      color: var(--text-muted);
      font-family: var(--font-narrative);
    }

    .ld-conditions {
      margin-top: var(--space-xs);
      font-size: 0.82rem;
      line-height: 1.5;
      color: var(--text-prose, #e2e8f0);
    }

    .ld-section {
      margin-top: var(--space-md);
    }

    .ld-section-label {
      font-size: 0.6rem;
      letter-spacing: 0.12em;
      color: var(--gold);
      font-weight: 700;
      margin-bottom: var(--space-xs);
    }

    .ld-quest-list {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: var(--space-xs);
    }

    .ld-quest-list li {
      font-size: 0.85rem;
      color: var(--text-prose, #e2e8f0);
      font-family: var(--font-narrative);
      line-height: 1.4;
      padding-left: var(--space-md);
      position: relative;
    }

    .ld-quest-list li::before {
      content: '\2023';
      position: absolute;
      left: 0;
      color: var(--gold);
    }

    .ld-quest-list li b {
      color: var(--gold);
    }
  </style>
</location-dialog>
